<template>
  <router-link v-slot="{ href, navigate, isActive, isExactActive }" :to="toValue" custom>
    <component
      :is="isLink ? 'a' : 'button'"
      :href="getHrefValue(href)"
      :class="['flex min-h-9 w-full items-center gap-x-3.5 text-left leading-tight tracking-[0.01em]', $attrs.class]"
      @click.prevent="click(navigate)"
    >
      <slot name="icon" v-bind="{ isActive, isExactActive }">
        <VcIcon
          v-if="link.icon"
          :name="link.icon"
          :size="32"
          :class="[
            isLink && isHighlighted(isActive, isExactActive)
              ? 'text-[--mobile-menu-icon-active-color]'
              : 'text-[--mobile-menu-icon-color]',
          ]"
        />
      </slot>

      <span
        :class="[
          'line-clamp-3 break-words',
          isLink && !isExternalLink && isHighlighted(isActive, isExactActive)
            ? 'text-[--mobile-menu-link-active-color]'
            : 'text-[--mobile-menu-link-color]',
        ]"
      >
        <slot v-bind="{ isActive, isExactActive, formattedText: formatTextFunction(link.title) }" />
      </span>

      <VcBadge v-if="count" variant="solid-light" color="neutral" rounded>
        {{ $n(count, { style: "decimal", notation: "compact" }) }}
      </VcBadge>

      <VcIcon v-if="isParent" class="ml-auto text-[--mobile-menu-navigation-color]" name="chevron-right" />
    </component>
  </router-link>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { getLinkAttr, isMenuLinkActive } from "@/core/utilities";
import type { ExtendedMenuLinkType } from "@/core/types";
import type { NavigationFailure } from "vue-router";

interface IEmits {
  (event: "select"): void;
  (event: "close"): void;
}

interface IProps {
  link: ExtendedMenuLinkType;
  count?: number;
  formatTextFunction?: (text: string | undefined) => string;
}

const emit = defineEmits<IEmits>();
const props = withDefaults(defineProps<IProps>(), {
  count: 0,
  formatTextFunction: (text: string | undefined) => text ?? "",
});

const route = useRoute();

const isParent = computed<boolean>(() => !!props.link.children?.length);
const isLink = computed<boolean>(() => !!props.link.route || !!props.link.isCatalogItem);

// The same rule as the account rail.
function isHighlighted(isActive: boolean, isExactActive: boolean): boolean {
  return isMenuLinkActive(props.link, route, isActive || isExactActive);
}

function click(navigate: () => Promise<void | NavigationFailure>) {
  if (isParent.value) {
    emit("select");
  } else {
    if (isExternalLink.value) {
      window.open(props.link.route as string, "_blank")?.focus();
    } else {
      void navigate();
    }
    emit("close");
  }
}

const isExternalLink = computed(() => {
  return isLink.value && "externalLink" in getLinkAttr(props.link.route);
});

function getHrefValue(href?: string) {
  if (isExternalLink.value) {
    return props.link.route;
  }
  return href;
}

const toValue = computed(() => {
  if (isExternalLink.value) {
    return "";
  }
  return props.link.route ?? "#";
});
</script>
